/**
 * This editor's side of a hosted session (hosted-session.ts): the tools it declares, and its answer to each
 * `tool_request`, in the MCP result shape. The chat panel and the bench share it, so the bench runs what a creator
 * runs.
 *
 * A tool result is a string (tools/types.ts): text, or a screenshot's data URL. Text goes as text; a screenshot as an
 * MCP `image`.
 *
 * A `stage: 'context'` request asks what the specialist writing a call's code needs (ml-services
 * src/agent/specialist.ts): the tool's own scene facts (`specialist.context`). The gateway binds the reference files
 * itself, from the turn's `fetch_url` reads.
 */

import type { DeclaredTool, ToolAnswer, ToolRequest } from './hosted-session';
import { documentRegionSchema, documentRenderSchema } from './document-region';
import { describeToolImage, readToolImage } from './tool-image';
import { isToolErrorResult } from './tool-errors';
import { executeTool, getToolDefinition, type ToolContext, type ToolDefinition } from './tools/tool-registry';

/** A specialist's `references`, as this editor declares them; the gateway binds them (ml-services src/agent/reads.ts) and holds their text. */
const referenceSchema = {
    type: 'array',
    maxItems: 8,
    items: {
        type: 'object',
        properties: { region: documentRegionSchema, render: documentRenderSchema, url: { type: 'string' }, pages: { type: 'string' } },
        required: ['url'],
        additionalProperties: false,
    },
};

/** The tools as this editor declares them: each schema as its executor takes it, and `references` on a specialist's tool. */
export function declareTools(defs: ToolDefinition[]): DeclaredTool[] {
    return defs.map(def => {
        const schema = def.tool.input_schema as Record<string, unknown>;
        return {
            name: def.tool.name,
            inputSchema: def.specialist
                ? { ...schema, properties: { ...(schema.properties as Record<string, unknown> | undefined), references: referenceSchema } }
                : schema,
        };
    });
}

const text = (value: string): Record<string, unknown> => ({ type: 'text', text: value });

/** A tool's result string as an MCP result. */
export function answerOf(name: string, result: string): ToolAnswer {
    const image = readToolImage(result);
    if (image) return { content: [text(describeToolImage(name, image)), { type: 'image', data: image.base64, mimeType: image.mime }] };
    return { content: [text(result)], ...(isToolErrorResult(result) ? { isError: true } : {}) };
}

/** A specialized call's context: the tool's scene facts. */
async function contextOf(request: ToolRequest, ctx: ToolContext): Promise<ToolAnswer> {
    const facts = await getToolDefinition(request.tool)?.specialist?.context?.(request.input, ctx) ?? {};
    return { content: [text(`Context for ${request.tool}.`)], structuredContent: facts };
}

/**
 * Answer one request. `run` executes a tool (the chat panel records the call and the objects it made; the bench
 * logs it); the context stage runs no tool.
 */
export async function answerToolRequest(
    request: ToolRequest, ctx: ToolContext,
    run: (name: string, input: Record<string, unknown>) => Promise<string> = (name, input) => executeTool(name, input, ctx),
): Promise<ToolAnswer> {
    try {
        if (request.stage === 'context') return await contextOf(request, ctx);
        return answerOf(request.tool, await run(request.tool, request.input));
    } catch (err) {
        return { content: [text(`Error: ${err instanceof Error ? err.message : String(err)}`)], isError: true };
    }
}
